import React, { useEffect, useCallback, useState } from 'react';
import { Form, Input, Spin } from 'antd';
import { useDispatch, useSelector } from 'react-redux';
import {
  validateIndividualIncome,
  validateFamilyIncome,
  updateFormField,
} from 'store/forms/actions';
import { validAFM, isBeneficiary } from 'utils';
import IncomeValidationErrorModal from './IncomeValidationErrorModal';

// Error code mappings from the PDF (exact translations from official documentation)
const ERROR_MESSAGES = {
  // Business logic errors (from Table 2 in PDF)
  1: 'Δεν υπάρχουν πληροφορίες για το ζητούμενο έτος YYYY',
  2: 'ΜΗ ΕΓΚΥΡΟΣ / ΕΝΕΡΓΟΣ ΑΦΜ ΦΥΣΙΚΟΥ ΠΡΟΣΩΠΟΥ',
  3: 'Ο ΑΦΜ ΑΝΗΚΕΙ ΣΕ ΑΠΟΒΙΩΣΑΝΤΑ',
  4: 'ΕΚΚΡΕΜΕΙ ΔΗΛΩΣΗ ΓΙΑ ΕΚΚΑΘΑΡΙΣΗ ΣΤΗ ΔΟΥ',
  5: 'ΔΕΝ ΕΧΕΙ ΥΠΟΒΛΗΘΕΙ ΔΗΛΩΣΗ ΦΕΦΠ',

  // Generic system errors (from Table 1 in PDF)
  GEN_AUDIT_VALIDATION_INV_DATA_OR_UNAUTH_OPER:
    'Τα διαπιστευτήρια δεν έχουν εξουσιοδότηση χρήσης της συγκεκριμένης διαδικτυακής λειτουργίας',
  GEN_AUDIT_VALIDATION_INVALID_AUDIT_DATA:
    'Παρουσιάστηκε σφάλμα κατά τον έλεγχο των audit πεδίων της κλήσης',
  GEN_INVALID_DATA:
    'Παρουσιάστηκε σφάλμα κατά τον έλεγχο εγκυρότητας των πεδίων εισόδου της υπηρεσίας',
  GEN_COMMUNICATION_ERROR:
    'Παρουσιάστηκε σφάλμα κατά την επικοινωνία με τη διαδικτυακή υπηρεσία του εξωτερικού φορέα',
  GEN_DATABASE_CONNECTION_ERROR:
    'Παρουσιάστηκε σφάλμα κατά τη σύνδεση με τη Βάση Δεδομένων',
  GEN_GENERAL_ERROR: 'Παρουσιάστηκε απροσδιόριστο σφάλμα',
  GEN_AUTHORIZATION_MAX_OPEN_CALLS_EXCEEDED:
    'Έχει γίνει υπέρβαση του ημερήσιου ορίου κλήσεων',

  // Default fallback
  default: 'Σφάλμα επικύρωσης εισοδήματος',
};

/**
 * IncomeValidationField - A specialized input field that validates both individual and family income
 * against government tax service (GSIS) and automatically sets the validation switch
 *
 * @param {object} form - Ant Design form instance
 * @param {string} fieldName - Name of the field in the form (default: 'vulnerableHousehold')
 * @param {boolean} disabled - Whether the field is disabled
 * @param {boolean} viewMode - Whether in view-only mode
 * @param {object} fieldProps - Additional props to pass to the input
 */
const IncomeValidationField = ({
  form,
  fieldName = 'vulnerableHousehold',
  disabled = false,
  viewMode = false,
  ...fieldProps
}) => {
  const dispatch = useDispatch();
  const individualIncomeValidation = useSelector(
    state => state.forms.individualIncomeValidation
  );
  const familyIncomeValidation = useSelector(
    state => state.forms.familyIncomeValidation
  );
  const audit = useSelector(state => state.forms.audit);
  const user = useSelector(state => state.user); // Get user from Redux
  const carrierLegalVat = useSelector(state => state?.forms?.carrier?.legalVat); // Get VAT from carrier state

  // State for error modal
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [errorModalData, setErrorModalData] = useState({
    individualError: null,
    familyError: null,
    hasSystemError: false,
  });

  // Helper function to check individual income threshold and format result
  const checkIndividualIncomeThreshold = useCallback((responseData) => {
    if (!responseData?.getPersonalIncomeStatusByLimitOutputRecord) {
      return null;
    }
    const { orioEisodhmatos, endeixhEisodhmatos } = responseData.getPersonalIncomeStatusByLimitOutputRecord;
    const INDIVIDUAL_THRESHOLD = 5000;
  
    // Use endeixhEisodhmatos to determine eligibility
    const isBelowOrEqual = endeixhEisodhmatos === "1"; // 1 means ≤ threshold
  
    return {
      isAboveThreshold: !isBelowOrEqual,
      threshold: INDIVIDUAL_THRESHOLD.toString(),
      comparison: isBelowOrEqual ? "≤" : ">",
      message: isBelowOrEqual 
        ? `ατομικό ≤ ${INDIVIDUAL_THRESHOLD}€`
        : `ατομικό > ${INDIVIDUAL_THRESHOLD}€`,
        actualIncome: isBelowOrEqual ? 5000 : 5001 
    };
  }, []);

  // Helper function to check family income threshold and format result
  const checkFamilyIncomeThreshold = useCallback(responseData => {
    if (!responseData?.getKlimakaEisodimaOutputRecord) {
      return null;
    }

    const { klimaka } = responseData.getKlimakaEisodimaOutputRecord;

    // According to specs: klimaka 1 or 2 means ≤ 10000€, others mean > 10000€
    const isEligible = klimaka === 1 || klimaka === 2;

    return {
      isAboveThreshold: !isEligible,
      threshold: '10000',
      comparison: isEligible ? '≤' : '>',
      message: isEligible ? `οικογενειακό ≤ 10000€` : `οικογενειακό > 10000€`,
      //actualIncome: isEligible ? 10000 : 10001 // Approximate for logic
      isEligible,
    };
  }, []);

  // Updated function to determine validation switch value based on new income rules
  // const determineValidationSwitchValue = useCallback((individualCheck, familyCheck) => {
  //   // New logic: BOTH conditions must be true for vulnerable household
  //   // Individual ≤ 5,000 AND Family ≤ 10,000 = Vulnerable (ΝΑΙ)

  //   // Check if we have both individual and family data
  //   if (individualCheck && familyCheck) {
  //     // Both incomes must be within thresholds
  //     const individualEligible = individualCheck.actualIncome <= 5000;
  //     const familyEligible = familyCheck.actualIncome <= 10000;

  //     return individualEligible && familyEligible; // Both must be true
  //   }

  //   // If we dont have both pieces of data, we cannot determine eligibility
  //   // This handles cases where one or both API calls failed
  //   if (!individualCheck || !familyCheck) {
  //     return false; // Default to not vulnerable when data is incomplete
  //   }

  //   // Fallback
  //   return false;
  // }, []);

  const determineValidationSwitchValue = useCallback(
    (individualCheck, familyCheck) => {
      if (individualCheck && familyCheck) {
        const individualEligible = individualCheck.actualIncome <= 5000;
        const familyEligible = familyCheck.isEligible; 
        return individualEligible && familyEligible;
      }
      return false;
    },
    []
  );

  // Get user-friendly error message
  const getErrorMessage = useCallback(error => {
    if (typeof error === 'string') {
      return ERROR_MESSAGES[error] || ERROR_MESSAGES.default;
    }
    return ERROR_MESSAGES.default;
  }, []);

  // Helper function to extract error from API response
  const extractErrorFromResponse = useCallback(
    response => {
      // Check if response has errorRecord with errorDescr
      if (response?.errorRecord?.errorDescr) {
        return response.errorRecord.errorDescr;
      }

      // Check if response has errorRecord with errorCode for fallback
      if (response?.errorRecord?.errorCode) {
        return getErrorMessage(response.errorRecord.errorCode);
      }

      return null;
    },
    [getErrorMessage]
  );

  // Helper function to determine if error is a system error (500, network, etc.)
  const isSystemError = useCallback(error => {
    // Check for HTTP status codes or network errors
    if (typeof error === 'object' && error?.status) {
      return error.status >= 500;
    }

    // Check for network/connection errors in error message
    if (typeof error === 'string') {
      return (
        error.includes('Network') ||
        error.includes('fetch') ||
        error.includes('Connection') ||
        error.includes('500') ||
        error.includes('Internal Server Error') ||
        error === 'GEN_COMMUNICATION_ERROR' ||
        error === 'GEN_DATABASE_CONNECTION_ERROR'
      );
    }

    return false;
  }, []);

  // Combined income validation handler
  const handleValidateIncome = useCallback(async () => {
    try {
      if (!carrierLegalVat || !validAFM(carrierLegalVat)) {
        return;
      }

      const currentYear = new Date().getFullYear();
      const individualYear =
        currentYear - process.env.REACT_APP_INDIVIDUAL_YEAR;
      const familyYear = currentYear - process.env.REACT_APP_FAMILY_YEAR;

      // Call both services in parallel with different years
      const [individualResponse, familyResponse] = await Promise.allSettled([
        dispatch(
          validateIndividualIncome({
            year: individualYear,
            afm: carrierLegalVat,
          })
        ),
        dispatch(
          validateFamilyIncome({
            year: familyYear,
            afm: carrierLegalVat,
          })
        ),
      ]);

      // Check for errors and prepare modal data
      let individualError = null;
      let familyError = null;
      let hasSystemError = false;

      // Handle individual income response
      if (individualResponse.status === 'rejected') {
        const error = individualResponse.reason;
        if (isSystemError(error)) {
          hasSystemError = true;
        } else {
          individualError = getErrorMessage(error);
        }
      } else if (individualResponse.status === 'fulfilled') {
        // Check if the successful response contains an error
        const responseError = extractErrorFromResponse(
          individualResponse.value
        );
        if (responseError) {
          if (isSystemError(responseError)) {
            hasSystemError = true;
          } else {
            individualError = responseError;
          }
        }
      }

      // Handle family income response
      if (familyResponse.status === 'rejected') {
        const error = familyResponse.reason;
        if (isSystemError(error)) {
          hasSystemError = true;
        } else {
          familyError = getErrorMessage(error);
        }
      } else if (familyResponse.status === 'fulfilled') {
        // Check if the successful response contains an error
        const responseError = extractErrorFromResponse(familyResponse.value);
        if (responseError) {
          if (isSystemError(responseError)) {
            hasSystemError = true;
          } else {
            familyError = responseError;
          }
        }
      }

      // Process successful responses REGARDLESS of whether some failed
      const individualCheck =
        individualResponse.status === 'fulfilled' &&
        !extractErrorFromResponse(individualResponse.value)
          ? checkIndividualIncomeThreshold(individualResponse.value)
          : null;
      const familyCheck =
        familyResponse.status === 'fulfilled' &&
        !extractErrorFromResponse(familyResponse.value)
          ? checkFamilyIncomeThreshold(familyResponse.value)
          : null;

      // Build message parts separately - render what we have
      let messageParts = [];
      let finalMessage;
      let validationSwitchValue;

      // Check if we have both individual and family data for complete validation
      if (individualCheck && familyCheck) {
        // We have both pieces of data - can make full determination
        messageParts.push(
          `ατομικό ${individualCheck.comparison} ${individualCheck.threshold}€`
        );
        messageParts.push(
          `οικογενειακό ${familyCheck.comparison} ${familyCheck.threshold}€`
        );

        finalMessage = messageParts.join(' / ');
        validationSwitchValue = determineValidationSwitchValue(
          individualCheck,
          familyCheck
        );
      } else if (individualCheck && !familyCheck) {
        // Only individual data available - insufficient for determination
        messageParts.push(
          `ατομικό ${individualCheck.comparison} ${individualCheck.threshold}€`
        );
        messageParts.push('Δεν υπάρχει οικογενειακό εισόδημα');

        finalMessage = messageParts.join(' / ');
        validationSwitchValue = false; // Cannot be vulnerable without both pieces of data
      } else if (!individualCheck && familyCheck) {
        // Only family data available - insufficient for determination
        messageParts.push('Δεν υπάρχει ατομικό εισόδημα');
        messageParts.push(
          `οικογενειακό ${familyCheck.comparison} ${familyCheck.threshold}€`
        );

        finalMessage = messageParts.join(' / ');
        validationSwitchValue = false; // Cannot be vulnerable without both pieces of data
      } else {
        // Neither API succeeded - show fallback message
        finalMessage = 'Δεν υπάρχουν δεδομένα';
        validationSwitchValue = false; // Default to false when no data available
      }

      // Always update the fields (either with data or fallback message)
      dispatch(
        updateFormField({
          group: 'audit',
          key: fieldName,
          value: finalMessage,
        })
      );

      dispatch(
        updateFormField({
          group: 'audit',
          key: 'vulnerableHouseholdAud',
          value: validationSwitchValue,
        })
      );

      // Update form fields
      form.setFieldsValue({
        [fieldName]: finalMessage,
        vulnerableHouseholdAud: validationSwitchValue,
      });

      // Show error modal AFTER processing successful data
      // Only show if there are errors AND user is NOT a beneficiary AND not in view mode
      if (
        (individualError || familyError || hasSystemError) &&
        !isBeneficiary(user?.groups) &&
        !viewMode
      ) {
        setErrorModalData({
          individualError,
          familyError,
          hasSystemError,
        });
        setShowErrorModal(true);
      }
    } catch (error) {
      // Catch any unexpected errors - only show modal if user is not a beneficiary and not in view mode
      console.error('Income validation error:', error);
      if (!isBeneficiary(user?.groups) && !viewMode) {
        setErrorModalData({
          individualError: null,
          familyError: null,
          hasSystemError: true,
        });
        setShowErrorModal(true);
      }
    }
  }, [
    dispatch,
    carrierLegalVat,
    form,
    fieldName,
    checkIndividualIncomeThreshold,
    checkFamilyIncomeThreshold,
    determineValidationSwitchValue,
    getErrorMessage,
    isSystemError,
    user?.groups,
    viewMode,
  ]);

  // Trigger validation when VAT changes
  useEffect(() => {
    if (validAFM(carrierLegalVat)) {
      handleValidateIncome();
    }
  }, [carrierLegalVat, handleValidateIncome]);

  // Determine field status and validation state
  const getValidationState = useCallback(() => {
    const isLoading =
      individualIncomeValidation.loading || familyIncomeValidation.loading;
    const hasError =
      individualIncomeValidation.error || familyIncomeValidation.error;
    const isValidated =
      individualIncomeValidation.validated && familyIncomeValidation.validated;

    if (isLoading) {
      return {
        validateStatus: 'validating',
        help: 'Επικύρωση εισοδήματος...',
        placeholder: 'Επικύρωση εισοδήματος...',
      };
    }

    if (hasError) {
      // Don't show error in form help text since we have the modal
      return {
        validateStatus: 'error',
        help: 'Σφάλμα επικύρωσης - Ελέγξτε τα στοιχεία',
        placeholder: '',
      };
    }

    if (isValidated && audit[fieldName]) {
      return {
        validateStatus: 'success',
        help: 'Επικύρωση ολοκληρώθηκε επιτυχώς',
        placeholder: '',
      };
    }

    // Handle partial validation (individual only)
    if (individualIncomeValidation.validated && audit[fieldName]) {
      return {
        validateStatus: 'success',
        help: 'Επικύρωση ατομικού εισοδήματος ολοκληρώθηκε επιτυχώς',
        placeholder: '',
      };
    }

    return {
      validateStatus: '',
      help: '',
      placeholder: '',
    };
  }, [individualIncomeValidation, familyIncomeValidation, audit, fieldName]);

  const validationState = getValidationState();

  return (
    <>
      <Form.Item
        validateStatus={validationState.validateStatus}
        help={validationState.help}
        className="income-validation-field"
      >
        <Input
          disabled={disabled}
          placeholder={validationState.placeholder}
          style={
            viewMode ? { pointerEvents: 'none', cursor: 'not-allowed' } : {}
          }
          suffix={
            individualIncomeValidation.loading ||
            familyIncomeValidation.loading ? (
              <Spin size="small" />
            ) : null
          }
          value={audit[fieldName] || ''}
          readOnly
          {...fieldProps}
        />
      </Form.Item>

      {/* Error Modal - Only render for non-beneficiary users and not in view mode */}
      {!isBeneficiary(user?.groups) && !viewMode && (
        <IncomeValidationErrorModal
          isVisible={showErrorModal}
          onClose={() => setShowErrorModal(false)}
          individualError={errorModalData.individualError}
          familyError={errorModalData.familyError}
          hasSystemError={errorModalData.hasSystemError}
        />
      )}
    </>
  );
};

export default IncomeValidationField;
